import React, { useEffect, useState } from 'react';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { Redirect, useParams, useHistory } from 'react-router-dom';
import { Grid, Paper, Snackbar } from '@material-ui/core';
import MuiAlert, { AlertProps } from '@material-ui/lab/Alert';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { styles } from '../../components/RestaurantEdit/styles';
import {
  editEntity as editRestaurant,
  patchRestaurantAttributes,
  patchRestaurantServices,
} from '../../redux/actions/restaurantsActions';
import { RestaurantEditHeader } from '../../components/RestaurantEdit/RestaurantEditHeader';
import { RestaurantEditImages } from '../../components/RestaurantEdit/RestaurantEditImages';
import { RestaurantEditInfo } from '../../components/RestaurantEdit/RestaurantEditInfo';
import { RestaurantEditContactInfo } from '../../components/RestaurantEdit/RestaurantEditContactInfo';
import {
  IRestaurant,
  IRestaurantEdit,
  IRestaurantEmailEdit,
  IRestaurantPhoneEdit,
  OPERATION_TYPES,
} from '../../components/RestaurantEdit/interfaces';
import AttributesForm from '../../components/RestaurantDetailsTab/components/AttributesForm';
import { snackbarShowMessage } from '../../redux/actions/snackbarActions';

interface IProps {
  history: ReturnType<typeof useHistory>;
}

interface IRouteParams {
  id?: string;
}

// TODO: get services from an endpoint
const restaurantServices = [
  { restaurantKey: 'hasDineIn', name: 'Dine In' },
  { restaurantKey: 'hasTakeOut', name: 'Take Out' },
  { restaurantKey: 'hasDriveThru', name: 'Drive Thru' },
  { restaurantKey: 'hasDigitalDriveThru', name: 'Mobile Order Drive Thru' },
  { restaurantKey: 'hasCurbsideSpots', name: 'Curbside Spot' },
  { restaurantKey: 'hasCurbsidePickups', name: 'Curbside Pickup' },
  { restaurantKey: 'doesHomeDelivery', name: 'Home Delivery' },
  { restaurantKey: 'hasMobileOrdering', name: 'Mobile Ordering' },
  { restaurantKey: 'servesBreakfast', name: 'Breakfast' },
  { restaurantKey: 'hasFullMenu', name: 'Full Menu' },
  { restaurantKey: 'hasLateNightMenu', name: 'Late-Night Menu' },
];
const restaurantServicesOther = [
  { restaurantKey: 'hasSuggestiveSell', name: 'Suggestive Sell' },
  { restaurantKey: 'hasODMB', name: 'ODMB' },
  { restaurantKey: 'hasWIFI', name: 'WIFI' },
  { restaurantKey: 'hasDigitalKiosk', name: 'Digital Kiosk' },
];

const replaceYesNoValues = restaurant => {
  return [...restaurantServices, ...restaurantServicesOther].reduce(
    (acc, service) => {
      acc[service.restaurantKey] =
        restaurant[service.restaurantKey] === 'Yes' ? 'Y' : 'N';
      return acc;
    },
    {},
  );
};

const removeUnusedPhonesOrEmails = (
  arr: (IRestaurantPhoneEdit | IRestaurantEmailEdit)[],
) => {
  return arr.filter(
    objVal => objVal.operation !== OPERATION_TYPES.DELETE || objVal.id !== null,
  );
};

const Alert: React.FC<AlertProps> = props => {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
};

const RestaurantEdit: React.FC<IProps> = props => {
  const classes = styles();
  const dispatch: any = useDispatch();
  const { t } = useTranslation();
  const methods = useForm({ mode: 'all' });
  const { control, formState } = methods;
  const { isDirty, isValid } = formState;
  const params = useParams<IRouteParams>();
  const { thisUser } = useSelector(
    (state: RootStateOrAny) => state.UserReducer,
  );
  const attributes = useSelector(
    (state: RootStateOrAny) => state.AttributesReducer.entities || [],
  );
  const services = useSelector(
    (state: RootStateOrAny) => state.ServicesReducer.entities || [],
  );
  const restaurant: any = useSelector<IRestaurant>(
    (state: RootStateOrAny) => state.RestaurantsReducer.entity,
  );
  const [restaurantEdit, setRestaurantEdit] = useState<IRestaurantEdit>(() => {
    return {
      restaurantId: restaurant.RestaurantId,
      restaurantStatus: restaurant.RestaurantStatus,
      addressLine1: restaurant.RestaurantAddressLine1,
      addressLine2: restaurant.RestaurantAddressLine2 || '',
      landmark: restaurant.Landmark || '',
      streetCornerLocation: restaurant.Crosstreet || '',
      city: restaurant.RestaurantAddressCity || '',
      emails: restaurant.emails?.map(email => ({
        operation: OPERATION_TYPES.UPDATE,
        id: email.RestaurantEmailId,
        value: email.RestaurantEmail,
        type: email.RestaurantEmailTypeId,
      })),
      phones: restaurant.phones?.map(phone => ({
        operation: OPERATION_TYPES.UPDATE,
        id: phone.RestaurantPhoneNumberId,
        number: phone.RestaurantPhoneNumber,
        type: phone.RestaurantPhoneTypeId,
        extension: phone.PhoneExtension,
      })),
      hasDineIn: restaurant.HasDineIn,
      hasTakeOut: restaurant.HasTakeOut,
      hasDriveThru: restaurant.HasDriveThru,
      hasDigitalDriveThru: restaurant.HasDigitalDriveThru,
      hasCurbsideSpots: restaurant.HasCurbsideSpots,
      hasCurbsidePickups: restaurant.HasCurbsidePickups,
      doesHomeDelivery: restaurant.DoesHomeDelivery,
      hasMobileOrdering: restaurant.HasMobileOrdering,
      servesBreakfast: restaurant.ServesBreakfast,
      hasFullMenu: restaurant.HasFullMenu,
      hasLateNightMenu: restaurant.HasLateNightMenu,
      hasSuggestiveSell: restaurant.HasSuggestiveSell,
      hasODMB: restaurant.HasODMB,
      hasWIFI: restaurant.HasWIFI,
      hasDigitalKiosk: restaurant.HasDigitalKiosk,
    };
  });
  const [customErrors, setCustomErrors] = useState<object>({});
  const [successfullyEdited, setSuccessfullyEdited] = useState<boolean>(false);

  useEffect(() => {
    if (typeof params.id === 'undefined') {
      props.history.goBack();
    }

    setRestaurantEdit(prevRestaurant => ({
      ...prevRestaurant,
      ...replaceYesNoValues(prevRestaurant),
    }));
  }, []);

  const handleCancelEdit = () => {
    props.history.goBack();
  };

  const handleSwitchChange = (name, checked) => {
    setRestaurantEdit(prevRestaurant => ({
      ...prevRestaurant,
      [name]: checked,
    }));
  };

  const handleError = (name: string, isValid: boolean, message?: string) => {
    if (!name && typeof isValid === 'undefined') {
      return;
    }
    if (!isValid) {
      setCustomErrors(prevErrors => ({ ...prevErrors, [name]: message }));
    } else {
      if (!customErrors[name]) return;
      const updatedErrors = { ...customErrors };
      delete updatedErrors[name];
      setCustomErrors(updatedErrors);
    }
  };

  const handleChange = (
    name: string,
    value: string,
    valid: boolean,
    validMessage: string,
  ) => {
    setRestaurantEdit(prevRestaurant => ({
      ...prevRestaurant,
      [name]: value,
      [`change${name}`]: true,
    }));

    handleError(name, valid, validMessage);
  };
  useEffect(() => {
    // No-op: Default implementation
  }, [control]);
  const handlePhoneEmailChange = (
    updatedData,
    type: string,
    name: string,
    valid: boolean,
    errorMessage: string,
  ) => {
    setRestaurantEdit(prevData => ({
      ...prevData,
      [type]: updatedData,
      [`change${type}`]: true,
    }));

    handleError(name, valid, errorMessage);
  };

  const handleSave = async data => {
    const kioskError = getKioskError(data);
    if (kioskError) {
      setCustomErrors(kioskError);
      return;
    }

    const servicesPayload: any[] = [];
    const attributesPayload: any[] = [];

    const keys = Object.keys(data);
    keys.forEach(key => {
      const prefix = key.substr(0, 3);
      const isReadOnly = key.substr(3, 1) === '+';
      const newKey = key.substr(4);

      if (isReadOnly) {
        return;
      }

      switch (prefix) {
        case 'att':
          attributesPayload.push({
            value: data[key],
            code: newKey,
          });
          break;

        case 'ser':
          servicesPayload.push({
            value: data[key],
            code: newKey,
          });
          break;
      }
    });
    const driveThruInput = servicesPayload.filter(x => x.code === 'driveThru');
    let addDrivethru = {};
    if (driveThruInput.length > 0) {
      addDrivethru = {
        hasDriveThru: driveThruInput[0].value ? 'Y' : 'N',
      };
    }

    const digitalDriveThruInput = servicesPayload.filter(
      x => x.code === 'digitalDriveThru',
    );
    let addDigitalDriveThru = {};
    if (digitalDriveThruInput.length > 0) {
      addDigitalDriveThru = {
        hasDigitalDriveThru: digitalDriveThruInput[0].value ? 'Y' : 'N',
      };
    }

    const updatedRestaurantEdit = {
      ...restaurantEdit,
      phones: removeUnusedPhonesOrEmails(restaurantEdit.phones),
      emails: removeUnusedPhonesOrEmails(restaurantEdit.emails),
      ...addDrivethru,
      ...addDigitalDriveThru,
    };

    const restaurantEditRequest = dispatch(
      editRestaurant(updatedRestaurantEdit),
    );
    const restaurantServicesEditRequest = dispatch(
      patchRestaurantServices(restaurant.restaurant_id, servicesPayload),
    );
    const requestQueue = [restaurantServicesEditRequest, restaurantEditRequest];
    if (thisUser.hasRestaurantLearningPlans) {
      const restaurantAttributesEditRequest = dispatch(
        patchRestaurantAttributes(restaurant.restaurant_id, attributesPayload),
      );
      requestQueue.push(restaurantAttributesEditRequest);
    }

    Promise.all(requestQueue).then(data => {
      const success = data.every(result => result.status < 300);
      if (success) {
        setSuccessfullyEdited(true);
        window.setTimeout(handleCancelEdit, 3000);
      } else {
        setSuccessfullyEdited(false);
        setCustomErrors({
          VALIDATION_ALERT_ERROR: data.reduce(
            (acc, result) => result?.data?.message + ' ' + acc,
            '',
          ),
        });
      }
    });
  };

  const handleCloseAlert = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    handleError('VALIDATION_ALERT_ERROR', true);
  };
  const onError = () => {
    if (Object.keys(formState.errors)) {
      dispatch(snackbarShowMessage('app.common.fieldsRequired'));
    }
  };

  return thisUser.hasEditRestaurant ? (
    <div className={classes.wrapper}>
      <Paper>
        <Grid
          container
          direction="column"
          spacing={2}
          className={classes.mainContainer}
        >
          <FormProvider {...methods}>
            <form
              className={classes.fullWidth}
              onSubmit={methods.handleSubmit(handleSave, onError)}
            >
              <Grid item>
                <RestaurantEditHeader
                  handleCancelEdit={handleCancelEdit}
                  isValid={!Object.keys(customErrors).length && isValid}
                />
              </Grid>

              <Grid item>
                <Grid container>
                  <Grid item sm={12} md={7}>
                    <div style={{ width: '96%' }}>
                      <RestaurantEditInfo
                        restaurant={restaurant}
                        restaurantEdit={restaurantEdit}
                        errors={customErrors}
                        handleError={handleError}
                        onChange={handleChange}
                      />

                      <RestaurantEditContactInfo
                        phones={restaurantEdit.phones}
                        emails={restaurantEdit.emails}
                        onChange={handlePhoneEmailChange}
                        errors={customErrors}
                        handleError={handleError}
                      />

                      <AttributesForm
                        attributes={services}
                        title={t('restaurant.info.restaurantServices')}
                        prefix="ser"
                      />
                      <AttributesForm
                        attributes={attributes}
                        title={t('restaurant.info.edit.learningPlans')}
                        prefix="att"
                        disabled={!thisUser.hasRestaurantLearningPlans}
                      />
                    </div>
                  </Grid>

                  <Grid item sm={12} md={5}>
                    <RestaurantEditImages />
                  </Grid>
                </Grid>
              </Grid>
            </form>
          </FormProvider>
        </Grid>
      </Paper>

      <Snackbar
        open={!!customErrors['VALIDATION_ALERT_ERROR']}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
      >
        <Alert severity="error" onClose={handleCloseAlert}>
          {customErrors['VALIDATION_ALERT_ERROR']}
        </Alert>
      </Snackbar>
      <Snackbar
        open={successfullyEdited}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
      >
        <Alert severity="success" onClose={handleCloseAlert}>
          {t(
            'restaurant.info.edit.editSuccess',
            'The restaurant has been succesfully updated!',
          )}
        </Alert>
      </Snackbar>
    </div>
  ) : (
    <Redirect to={`/restaurants/${restaurantEdit.restaurantId}/info`} />
  );
};

export default RestaurantEdit;

function getKioskError(data) {
  const hasEditableDigitalKiosk = !!data['ser-digitalKiosk'];
  if (!hasEditableDigitalKiosk) return false;
  const hasKiosks = Object.entries(data)
    .filter(([key]) => /^ser(\+|-)numberOfKiosk_\d+$/.test(key))
    .some(p => !!Number(p[1]));
  if (hasKiosks) return false;
  const errorText =
    'There must be at least one kiosk when a restaurant has digital kiosk enabled';
  return { VALIDATION_ALERT_ERROR: errorText };
}
